import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { LayoutProvider } from '../hooks/useLayoutState';
import { privateRoutes, getRoutesForUser } from './privateRoutes.config.js';
import { useLocationHistory } from '../hooks/useLastLocation.js';
import userAuth from '../Config/userauth';
import { ProtectedRoute, PublicRoute } from '../Components/ProtectedRoute';
import { publicRoutes } from './publicRoutes.config';
import { Loading } from '../Components';

/**
 * Componente para manejar redirección de mantenimiento
 */
const MaintenanceRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const redirect = import.meta.env.VITE_MAINTENANCE === 'true';
    if (redirect) {
      window.location.href = 'https://www.lifeseguros.com.ar/mantenimiento/';
    }
  }, [navigate]);

  return null;
};

/**
 * Componente para renderizar rutas públicas
 */
const PublicRoutes = () => {
  return (
    <PublicRoute>
      <Routes>
        {publicRoutes.map(({ path, component: Component, exact }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<Loading />}>
                <Component />
              </Suspense>
            }
          />
        ))}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </PublicRoute>
  );
};

/**
 * Componente para renderizar rutas privadas según el tipo de usuario
 */
const PrivateRoutes = ({ isAdmin, requirePasswordChange, requireProducerSelection }) => {
  const location = useLocation();
  const { getLastLocation } = useLocationHistory();
  const userLastLocation = getLastLocation();

  // Determinar rutas permitidas según el estado del usuario
  const allowedRoutes = getRoutesForUser(isAdmin, requirePasswordChange, requireProducerSelection);

  // Determinar redirección inicial
  const getInitialRedirect = () => {
    if (location.pathname === '/') {
      if (isAdmin) return '/avanzadas';
      if (requirePasswordChange) return `/recover-reset?code=${userAuth.userCode()}&simulador=true`;
      if (requireProducerSelection) return '/simulate-producer';
      return '/home';
    }
    return null;
  };

  const initialRedirect = getInitialRedirect();

  // Para la ruta /simulate-producer, no requerir selección de productor para evitar bucle infinito
  const shouldRequireProducerSelection = requireProducerSelection && location.pathname !== '/simulate-producer';

  return (
    <ProtectedRoute
      requireAdmin={isAdmin}
      requirePasswordChange={requirePasswordChange}
      requireProducerSelection={shouldRequireProducerSelection}
      allowedRoutes={allowedRoutes.map(route => route.path)}
    >
      <Routes>
        {initialRedirect && (
          <Route path="/" element={<Navigate to={initialRedirect} replace />} />
        )}
        
        {allowedRoutes.map(({ path, component: Component, exact }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<Loading />}>
                <Component />
              </Suspense>
            }
          />
        ))}
        
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </ProtectedRoute>
  );
};

/**
 * Componente principal de routing
 */
export const Routing = () => {
  const location = useLocation();
  const { saveLastLocation } = useLocationHistory();
  
  const isAuth = userAuth.isAuthenticated();
  const isAdmin = userAuth.isAdmin();
  const requirePasswordChange = userAuth.requestChangePassword();
  const requireProducerSelection = userAuth.requestSelectProducer();

  // Guardar última ubicación para redirecciones
  useEffect(() => {
    saveLastLocation(location);
  }, [location, saveLastLocation]);

  // Configuración del menú (temporalmente vacío hasta implementar estado local)
  const menuConfig = {};

  return (
    <>
      <MaintenanceRedirect />
      <LayoutProvider pathname={location.pathname} menuConfig={menuConfig}>
        {!isAuth ? (
          <PublicRoutes />
        ) : (
          <PrivateRoutes
            isAdmin={isAdmin}
            requirePasswordChange={requirePasswordChange}
            requireProducerSelection={requireProducerSelection}
          />
        )}
      </LayoutProvider>
    </>
  );
};

export default Routing;
